import React, { useState, useCallback, useMemo } from 'react';
import { Search, FileText, TrendingUp, Hash, Download } from 'lucide-react';

const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'LIN', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];

const formatarMoeda = (valor) => {
    const num = parseFloat(valor) || 0;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatarInteiro = (valor) => {
    return (parseInt(valor) || 0).toLocaleString('pt-BR');
};

// Data local em YYYY-MM-DD (evita o deslocamento de fuso do toISOString).
const fmtData = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const brData = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

const inputStyle = {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', color: '#334155', backgroundColor: '#fff',
    outline: 'none', width: '100%', boxSizing: 'border-box',
};

const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
};

// Chip (pílula) clicável — padrão visual reutilizado para lojas e meios de pagamento.
const chipStyle = (ativo, cor = '#16a39c') => ({
    padding: '6px 14px', borderRadius: '20px',
    border: `2px solid ${ativo ? cor : '#e2e8f0'}`,
    background: ativo ? `${cor}14` : '#fff',
    color: ativo ? cor : '#94a3b8',
    fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s',
});

const FinanceiroFitaConsulta = () => {
    // Período padrão: 1º dia do mês corrente → ontem.
    // No 1º dia do mês, "ontem" cai no mês anterior; nesse caso o início recua para ontem
    // (vira um único dia) para não montar um intervalo invertido.
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    const ontemStr = fmtData(ontem);
    const primeiroDiaMes = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`;
    const inicioDefault = ontemStr < primeiroDiaMes ? ontemStr : primeiroDiaMes;

    const [inicio,     setInicio]     = useState(inicioDefault);
    const [fim,        setFim]        = useState(ontemStr);
    const [lojas,      setLojas]      = useState([]);   // chips multi-seleção (OR)
    const [meios,      setMeios]      = useState([]);   // chips de meio de pagamento (filtro no cliente)
    const [resultado,  setResultado]  = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro,       setErro]       = useState('');

    const token = localStorage.getItem('authToken');

    const toggleLoja = (loja) =>
        setLojas((cur) => (cur.includes(loja) ? cur.filter((l) => l !== loja) : [...cur, loja]));

    // Marca/desmarca um meio de pagamento (filtro de visualização). Múltiplos = OR.
    const toggleMeio = (nome) =>
        setMeios((cur) => (cur.includes(nome) ? cur.filter((m) => m !== nome) : [...cur, nome]));

    // Recorte no cliente pelos chips de meio de pagamento (vazio = todos).
    const registrosFiltrados = useMemo(() => {
        if (!resultado) return [];
        if (meios.length === 0) return resultado.registros;
        return resultado.registros.filter((r) => meios.includes(r.tender_name));
    }, [resultado, meios]);

    // Totais recalculados sobre o conjunto filtrado (cards, rodapé e export seguem os chips).
    const totais = useMemo(() => ({
        valor: registrosFiltrados.reduce((acc, r) => acc + (parseFloat(r.valor) || 0), 0),
        quantidade: registrosFiltrados.reduce((acc, r) => acc + (parseInt(r.quantidade) || 0), 0),
    }), [registrosFiltrados]);

    const exportarCSV = useCallback(() => {
        if (!resultado || registrosFiltrados.length === 0) return;

        const linhas = [
            ['Meio de Pagamento', 'Qtd. Vendas', 'Valor (R$)'],
            ...registrosFiltrados.map((r) => [
                r.tender_name || '',
                parseInt(r.quantidade) || 0,
                (parseFloat(r.valor) || 0).toFixed(2).replace('.', ','),
            ]),
            ['TOTAL', totais.quantidade, (totais.valor || 0).toFixed(2).replace('.', ',')],
        ];

        const csvContent = '﻿' + linhas
            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = `fita_${resultado.lojas.join('-')}_${resultado.inicio}_a_${resultado.fim}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [resultado, registrosFiltrados, totais]);

    const consultar = useCallback(async () => {
        if (!inicio || !fim) { setErro('Selecione o período (de/até) para consultar.'); return; }
        if (fim < inicio)    { setErro('A data final não pode ser anterior à inicial.'); return; }
        if (lojas.length === 0) { setErro('Selecione ao menos uma loja.'); return; }

        setCarregando(true); setErro(''); setResultado(null); setMeios([]);
        try {
            const qs = new URLSearchParams({ inicio, fim, lojas: lojas.join(',') });
            const res = await fetch(`/api/fita/consulta?${qs.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Erro ${res.status}`); }
            setResultado(await res.json());
        } catch (e) {
            setErro(e.message || 'Erro ao consultar dados.');
        } finally {
            setCarregando(false);
        }
    }, [inicio, fim, lojas, token]);

    const podeConsultar = inicio && fim && lojas.length > 0 && !carregando;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Consulta de Fita</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                    Pagamentos por meio, agrupados no período e nas lojas escolhidas. Cancelados excluídos.
                </p>
            </div>

            {/* FILTROS */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Período + botão consultar */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={labelStyle}>De</label>
                        <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)}
                            style={{ ...inputStyle, width: '170px' }} />
                    </div>
                    <div>
                        <label style={labelStyle}>Até</label>
                        <input type="date" value={fim} onChange={(e) => setFim(e.target.value)}
                            style={{ ...inputStyle, width: '170px' }} />
                    </div>
                    <button onClick={consultar} disabled={!podeConsultar}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', borderRadius: '8px', border: 'none', height: '42px', whiteSpace: 'nowrap', fontWeight: '800', fontSize: '14px', cursor: podeConsultar ? 'pointer' : 'not-allowed', transition: 'all 0.2s', backgroundColor: podeConsultar ? '#16a39c' : '#e2e8f0', color: podeConsultar ? '#fff' : '#94a3b8' }}>
                        <Search size={16} />
                        {carregando ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>

                {/* Lojas — chips multi-seleção (OR). Ao menos uma é obrigatória. */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                            Lojas {lojas.length > 0 ? `(${lojas.length}/${LOJAS.length})` : '(selecione)'}
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={() => setLojas([...LOJAS])} style={chipStyle(true, '#3b82f6')}>Todas</button>
                            <button onClick={() => setLojas([])} style={chipStyle(true, '#e11d48')}>Limpar</button>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {LOJAS.map((l) => (
                            <button key={l} onClick={() => toggleLoja(l)} style={chipStyle(lojas.includes(l), '#3b82f6')}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {erro && (
                <div style={{ padding: '14px 18px', borderRadius: '10px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', fontWeight: '600', fontSize: '14px' }}>
                    {erro}
                </div>
            )}

            {resultado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                            {resultado.lojas.length === 1
                                ? <>Loja <strong>{resultado.lojas[0]}</strong></>
                                : <><strong>{resultado.lojas.length} lojas</strong> ({resultado.lojas.join(', ')})</>}
                            {' · '}{brData(resultado.inicio)} – {brData(resultado.fim)}
                        </span>
                        <button onClick={exportarCSV} disabled={registrosFiltrados.length === 0}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', borderRadius: '8px', border: '1px solid #16a39c', backgroundColor: '#f0fdfa', color: '#16a39c', fontWeight: '700', fontSize: '13px', cursor: registrosFiltrados.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                            <Download size={15} /> Exportar CSV
                        </button>
                    </div>

                    {/* Meio de pagamento — chips de filtro (derivados do resultado). Vazio = todos. */}
                    {resultado.registros.length > 0 && (
                        <div>
                            <label style={{ ...labelStyle, marginBottom: '10px' }}>
                                Meio de pagamento {meios.length > 0 ? '(filtrando)' : '(todos)'}
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {resultado.registros.map((r) => (
                                    <button key={r.tender_name} onClick={() => toggleMeio(r.tender_name)}
                                        style={chipStyle(meios.includes(r.tender_name))}>
                                        {r.tender_name || '—'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* CARDS */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                        {[
                            { label: 'Total', valor: formatarMoeda(totais.valor), icon: <TrendingUp size={22} />, bg: '#f0fdfa', cor: '#16a39c' },
                            { label: 'Vendas', valor: formatarInteiro(totais.quantidade), icon: <Hash size={22} />, bg: '#eff6ff', cor: '#3b82f6' },
                            { label: 'Meios', valor: registrosFiltrados.length, icon: <FileText size={22} />, bg: '#f8fafc', cor: '#64748b' },
                        ].map((c) => (
                            <div key={c.label} style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: c.bg, color: c.cor }}>{c.icon}</div>
                                <div>
                                    <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>{c.label}</p>
                                    <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>{c.valor}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* TABELA */}
                    {registrosFiltrados.length === 0 ? (
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '48px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>
                            Nenhum registro encontrado.
                        </div>
                    ) : (
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            {['Meio de Pagamento', 'Qtd. Vendas', 'Valor'].map((h, i) => (
                                                <th key={h} style={{ padding: '12px 20px', textAlign: i === 0 ? 'left' : 'right', fontWeight: '800', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {registrosFiltrados.map((row, idx) => (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                <td style={{ padding: '12px 20px', color: '#0f172a', fontWeight: '600' }}>{row.tender_name || '—'}</td>
                                                <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontWeight: '500' }}>{formatarInteiro(row.quantidade)}</td>
                                                <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(row.valor)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr style={{ backgroundColor: '#f0fdfa', borderTop: '2px solid #0d7a74' }}>
                                            <td style={{ padding: '14px 20px', fontWeight: '800', color: '#0d7a74' }}>TOTAL</td>
                                            <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74' }}>{formatarInteiro(totais.quantidade)}</td>
                                            <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(totais.valor)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', fontWeight: '500' }}>
                                    Cancelados excluídos via <code>dtbSovosPortalNota</code>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FinanceiroFitaConsulta;
